/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useState } from 'react';
import { Box, VStack, Center, Spinner, Flex } from 'chakra-v3';
import { toaster } from '../components/ui/toaster';
import { paymentsApi } from '../service/api/payments/Api';
import { NoCredits } from '../components/payments/NoCredits';
import { EnterUpi } from '../components/payments/EnterUpi';
import { VerificationPending } from '../components/payments/VerificationPending';
import { VerifiedTransaction } from '../components/payments/VerifiedTransaction';
import { Text } from '../components/Text';
import UserHeader from '../components/headers/UserHeader';
import ErrorDisplay from '../components/Error';
import { getErrorInfo, isServerError, isRateLimitError } from '../utils/error';

// Define flow steps
type PaymentStep =
  | 'LOADING' // Initial state while fetching data
  | 'NO_CREDITS'
  | 'ENTER_UPI'
  | 'VERIFICATION_PENDING'
  | 'VERIFIED'
  | 'ERROR';

export const Payments: React.FC = () => {
  // State
  const [step, setStep] = useState<PaymentStep>('LOADING');
  const [upiId, setUpiId] = useState('');
  const [name, setName] = useState('');
  const [accountId, setAccountId] = useState<string | null>(null);

  // API Hooks
  const paymentSummary = paymentsApi.useGetPaymentSummaryQuery();
  const currentAccount = paymentsApi.useGetCurrentAccountQuery();
  const [addAccount, addAccountResult] = paymentsApi.useAddAccountMutation();
  const [verifyAccount, verifyAccountResult] = paymentsApi.useVerifyAccountMutation();

  // Fetch transactions only if verified
  const transactions = paymentsApi.useGetTransactionsQuery(undefined, {
    skip: step !== 'VERIFIED',
  });

  // Determine initial step on mount and when data changes
  React.useEffect(() => {
    if (paymentSummary.isLoading || currentAccount.isLoading) {
      return;
    }

    if (paymentSummary.isError || currentAccount.isError) {
      toaster.create({
        title: 'Error',
        description: 'Failed to load payment information. Please reload the page.',
        type: 'error',
      });
      setStep('ERROR');
      return;
    }

    // If no credits earned, show NO_CREDITS
    if ((paymentSummary.data?.total_earned ?? 0) === 0) {
      setStep('NO_CREDITS');
      return;
    }

    // Check current account status
    if (currentAccount.data) {
      if (currentAccount.data.status === 'VERIFIED') {
        setStep('VERIFIED');
      } else if (currentAccount.data.status === 'PENDING_VERIFICATION') {
        setStep('VERIFICATION_PENDING');
        setAccountId(currentAccount.data.id);
        // Populate state from API if available to show in UI
        if (currentAccount.data.meta?.account?.id) {
          setUpiId(currentAccount.data.meta.account.id);
        }
        if (currentAccount.data.meta?.name) {
          setName(currentAccount.data.meta.name);
        }
      } else {
        setStep('ENTER_UPI');
      }
    } else {
      // No current account found, so user needs to enter UPI
      setStep('ENTER_UPI');
    }
  }, [
    paymentSummary.data,
    currentAccount.data,
    paymentSummary.isLoading,
    currentAccount.isLoading,
    paymentSummary.isError,
    currentAccount.isError,
  ]);

  // Shared logic for adding an account
  const performAddAccount = async (upiIdInput: string, nameInput: string) => {
    try {
      const response = await addAccount({
        type: 'vpa',
        name: nameInput,
        account: {
          id: upiIdInput,
        },
      }).unwrap();

      setAccountId(response.id);
      return response;
    } catch (e: any) {
      console.error('Failed to add account', e);
      const { message, title } = getErrorInfo(e);

      if (isRateLimitError(e)) {
        toaster.create({
          title: title || 'Max attempts reached',
          description: 'Please try after 1 hour.',
          type: 'error',
        });
        throw e;
      }

      if (isServerError(e)) {
        toaster.create({
          title: title || 'Something went wrong',
          description: 'Please retry after some time.',
          type: 'error',
        });
        throw e;
      }

      toaster.create({
        title: title || 'Error',
        description: message,
        type: 'error',
      });
      throw e;
    }
  };

  // Handlers
  const handleUpiSubmit = async (upiIdInput: string, nameInput: string) => {
    try {
      await performAddAccount(upiIdInput, nameInput);
      setUpiId(upiIdInput);
      setName(nameInput);
      setStep('VERIFICATION_PENDING');
    } catch (e) {
      console.error('Failed to submit UPI details', e);
    }
  };

  const handleVerifyConfirm = async () => {
    if (!accountId) return;
    try {
      await verifyAccount({ id: accountId, body: { confirm: true } }).unwrap();
      setStep('VERIFIED');
    } catch (e: any) {
      console.error('Failed to verify account', e);
      const { message, title } = getErrorInfo(e);

      if (isRateLimitError(e)) {
        toaster.create({
          title: title || 'Max attempts reached',
          description: 'Please try after 1 hour.',
          type: 'error',
        });
        return;
      }

      if (isServerError(e)) {
        toaster.create({
          title: title || 'Verification failed',
          description: 'Something went wrong. Please retry after some time.',
          type: 'error',
        });
        return; // Stop execution on error
      }

      toaster.create({
        title: title || 'Error',
        description: message,
        type: 'error',
      });
    }
  };

  const handleVerifyDeny = async () => {
    try {
      // Just try to add the account again.
      // If rate limited, performAddAccount will handle the 429 error.
      await performAddAccount(upiId, name);
    } catch (e) {
      console.error('Failed to deny verification', e);
    }
  };

  const handleVerifyEdit = () => {
    setStep('ENTER_UPI');
  };

  return (
    <Box minH="100vh" bg="bg.primary">
      <UserHeader />
      <Box p={8} maxW="1200px" mx="auto">
        <VStack gap={8} align="stretch">
          <Center>
            {step === 'LOADING' && (
              <Center py={20}>
                <Flex gap={4} alignItems="center">
                  <Spinner size="lg" color="spinner.blue" />
                  <Text variant="body" color="text.primary">
                    Loading payment details...
                  </Text>
                </Flex>
              </Center>
            )}

            {step === 'ERROR' && (
              <ErrorDisplay
                message="Failed to load payment information."
                navigateButton={{ label: 'Reload', to: '/payments' }}
              />
            )}

            {step === 'NO_CREDITS' && <NoCredits />}

            {step === 'ENTER_UPI' && (
              <EnterUpi
                onSubmit={(upiIdInput, nameInput) => {
                  void handleUpiSubmit(upiIdInput, nameInput);
                }}
                isLoading={addAccountResult.isLoading}
                initialName={name}
                initialUpiId={upiId}
              />
            )}

            {step === 'VERIFICATION_PENDING' && (
              <VerificationPending
                onConfirm={() => {
                  void handleVerifyConfirm();
                }}
                onDeny={() => {
                  void handleVerifyDeny();
                }}
                onEdit={handleVerifyEdit}
                isLoading={verifyAccountResult.isLoading || addAccountResult.isLoading}
                upiId={upiId}
                name={name}
              />
            )}

            {step === 'VERIFIED' && (
              <VerifiedTransaction
                transactions={transactions.data?.transactions || []}
                isLoading={transactions.isLoading}
                error={transactions.error}
              />
            )}
          </Center>
        </VStack>
      </Box>
    </Box>
  );
};

export default Payments;
